import { useEffect, useState } from "react";
import { Fuel as FuelIcon } from "lucide-react";
import api from "../api";
import DataTable from "../components/DataTable";
import FuelDialog from "../components/FuelDialog";
import StatCard from "../components/StatCard";
import { fmtDate, fmtMoney, fmtNum } from "../utils/format";

export default function Fuel() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);

  const load = () => {
    api.get("/fuel").then((r) => setRows(r.data));
    api.get("/fuel/stats").then((r) => setStats(r.data));
  };
  useEffect(() => { load(); }, []);

  const totalCost = rows.reduce((s, f) => s + (f.total || 0), 0);
  const totalLiters = rows.reduce((s, f) => s + (f.liters || 0), 0);

  return (
    <div className="space-y-5" data-testid="fuel-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Carburant</h1>
          <p className="mt-1 text-sm text-slate-500">Chaque ravitaillement est ajouté automatiquement au coût de revient du véhicule.</p>
        </div>
        <FuelDialog onSaved={load} />
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={FuelIcon} label="Ravitaillements" value={rows.length} tone="cyan" testid="fuel-count" />
        <StatCard icon={FuelIcon} label="Litres totaux" value={`${fmtNum(totalLiters)} L`} tone="sky" testid="fuel-liters" />
        <StatCard icon={FuelIcon} label="Coût total" value={fmtMoney(totalCost)} tone="amber" testid="fuel-total-cost" />
        <StatCard icon={FuelIcon} label="Véhicules suivis" value={stats?.per_vehicle?.length || 0} tone="emerald" testid="fuel-vehicles" />
      </div>

      {stats && stats.per_vehicle.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="fuel-stats">
          <h3 className="font-heading mb-3 text-base font-semibold text-slate-800">Consommation par véhicule</h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {stats.per_vehicle.map((s) => (
              <div key={s.vehicle_id} className="rounded-lg border border-slate-100 bg-slate-50/60 p-4">
                <p className="font-semibold text-slate-800">{s.vehicle_label}</p>
                <div className="mt-2 grid grid-cols-3 gap-2 text-center">
                  <div><p className="text-[10px] uppercase text-slate-400">Conso moy.</p><p className="font-mono-num text-sm font-bold text-cyan-700">{s.consumption_per_100km != null ? `${s.consumption_per_100km} L/100` : "—"}</p></div>
                  <div><p className="text-[10px] uppercase text-slate-400">Coût/km</p><p className="font-mono-num text-sm font-bold">{s.cost_per_km != null ? fmtNum(s.cost_per_km) : "—"}</p></div>
                  <div><p className="text-[10px] uppercase text-slate-400">Total</p><p className="font-mono-num text-sm font-bold">{fmtNum(s.total_liters)} L</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <DataTable testidPrefix="fuel-table" rows={rows}
        searchKeys={["vehicle_label", "station", "driver"]}
        columns={[
          { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
          { key: "vehicle_label", label: "Véhicule", render: (r) => <span className="font-medium">{r.vehicle_label}</span> },
          { key: "mileage", label: "Km", render: (r) => fmtNum(r.mileage) },
          { key: "liters", label: "Litres", className: "text-right", render: (r) => <span className="font-mono-num">{fmtNum(r.liters)}</span> },
          { key: "price_per_liter", label: "Prix/L", className: "text-right", render: (r) => <span className="font-mono-num">{fmtNum(r.price_per_liter)}</span> },
          { key: "total", label: "Total", className: "text-right", render: (r) => <span className="font-mono-num font-semibold">{fmtMoney(r.total)}</span> },
          { key: "station", label: "Station", render: (r) => r.station || "—" },
          { key: "driver", label: "Conducteur", render: (r) => r.driver || "—" },
          { key: "created_by", label: "Par", render: (r) => <span className="text-xs text-slate-500">{r.created_by}</span> },
        ]}
      />
    </div>
  );
}
